import {Injectable} from '@angular/core';
import {AmberApi} from 'app/core/models/api-helpers';
import {Observable, ReplaySubject} from 'rxjs';
import {ActionButtonType, ButtonSize, DisplayType, WaterfallConfig} from './waterfall-config';
import {RequestService} from 'app/core/services/request.service';
import {ConfigService} from 'app/core/services/config.service';

@Injectable()
export class WaterfallService {

    constructor(private requestService: RequestService,
                private configService: ConfigService) {
    }

    defaultConfig(entryPoint: string, role: string[]): WaterfallConfig {
        let catalogInput = this.requestService.get(this.configService.get('server').url + this.configService.get('endpoints').agora.catalogs.domain.list.api,
            {'role': role}).extract(AmberApi.Data).map(response => response.map(item => {
            item['value'] = item.name;
            item['svg'] = item.catalogSvg;
            return item;
        })).shareReplay(1);
        let catalogButtonSubject = new ReplaySubject<any>(1);
        let topicsInput = catalogButtonSubject.switchMap(res => this.requestService.get(this.configService.get('server').url
            + this.configService.get('endpoints').agora.topics.inCategoryTopics.api + '/' + res.code, {'$title': 1})).extract(AmberApi.Data)
            .map(response => response.map(item => {
                item['value'] = item.title;
                item['svg'] = item.imageSvg;
                return item;
            }));
        let topicButtonSubject = new ReplaySubject<any>(1);
        let categoryInput = topicButtonSubject.withLatestFrom(catalogButtonSubject)
            .switchMap(([topic, code]) => this.requestService.get(this.configService.get('server').url +
                this.configService.get('endpoints').agora.categories.byTopicId.api + '/' + topic.id,
                {'catalogCode': code.code})).extract(AmberApi.Data);

        return {
            input: catalogInput,
            onItemClicked: catalogButtonSubject,
            display: {
                type: DisplayType.Buttons,
                subtitle: `Please select a catalog that best describes your ${entryPoint}.`,
                size: ButtonSize.Lg,
                numberOfColumns: 2,
                buttons: [{type: ActionButtonType.Back, name: 'Cancel'}],
                defaultSvg: '\<i class=\"fal fa-question-circle\"\>\<\/i\>',
                separators: [{
                    groupItems: catalogInput.map((catalog) => catalog.filter(item => !item.domainSpecific)),
                    title: 'MARKETPLACE CATALOGS'
                }, {
                    groupItems: catalogInput.map((catalog) => catalog.filter(item => item.domainSpecific)),
                    title: 'CUSTOM CATALOGS'
                }],
                showSeparator: true,
                showSvg: true
            },
            skipDefault: true,
            isRoot: true,
            child: WaterfallService.defaultTopicConfig(topicsInput, topicButtonSubject, categoryInput, entryPoint),
            levelTag: 'catalog',
            rootBreadcrumb: {parentChoice: 'Catalogs', ancestorHistory: []},
            nameExtractor: (item) => item.name
        }
    }

    private static defaultTopicConfig(topicsInput: Observable<any[]>, topicButtonSubject: ReplaySubject<any>,
                                      categoryInput: Observable<any[]>, entryPoint: string): WaterfallConfig {
        return {
            input: topicsInput,
            onItemClicked: topicButtonSubject,
            display: {
                type: DisplayType.Buttons,
                subtitle: 'Please select a topic within this catalog.',
                numberOfColumns: 2,
                buttons: [{type: ActionButtonType.Back, name: 'Back'}],
                defaultSvg: '\<i class=\"fal fa-question-circle\"\>\<\/i\>',
                separators: [],
                showSeparator: false,
                showSvg: true
            },
            isRoot: false,
            child: WaterfallService.defaultCategoryConfig(categoryInput, entryPoint),
            levelTag: 'topic',
            nameExtractor: (item) => item.title
        };
    }

    private static defaultCategoryConfig(categoryInput: Observable<any[]>, entryPoint: string): WaterfallConfig {
        return {
            input: categoryInput,
            onItemClicked: undefined,
            display: {
                type: DisplayType.Buttons,
                subtitle: `Please select the category that best describes your ${entryPoint}.`,
                size: ButtonSize.Sm,
                numberOfColumns: 2,
                buttons: [{type: ActionButtonType.Back, name: 'Back'}],
                defaultSvg: '\<i class=\"fal fa-question-circle\"\>\<\/i\>',
                separators: [],
                showSeparator: false,
                showSvg: false
            },
            isRoot: false,
            levelTag: 'category',
            nameExtractor: item => item.value
        }
    }

    internalWaterfallConfig(entryPoint: string = 'course'): WaterfallConfig {
        const url = this.configService.get('server').url + this.configService.get('endpoints').agora.customTopics.dictionary.api;
        let topicsInput = this.requestService.get(url).extract(AmberApi.Data)
            .map(response => response.map(item => {
                return item;
            }));
        let topicButtonSubject = new ReplaySubject<any>(1);
        let categoryInput = topicButtonSubject.switchMap(item => this.requestService.get(this.configService.get('server').url +
            this.configService.get('endpoints').agora.customTopics.categoryListByTopicCode.api + '/' + item.key)).extract(AmberApi.Data)
            .map(response => response.map(item => {
                item['value'] = item.name;
                return item;
            }));
        let response: WaterfallConfig = WaterfallService.defaultTopicConfig(topicsInput, topicButtonSubject, categoryInput, entryPoint);
        response.isRoot = true;
        response.rootBreadcrumb = {parentChoice: 'Topics', ancestorHistory: []};
        response.nameExtractor = (item) => item.value
        return response;
    }
}



// WEBPACK FOOTER //
// ./src/app/shared/components/waterfall/waterfall.service.ts